import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, switchMap } from 'rxjs/operators';
import { DateTime } from 'luxon';

import { StatusPerDay } from '../../../../domain/models/status-per-day';
import { BckpConstants } from '../../../../bckp.constants';
import { VboSuccessRateListDomainService } from '@apps/bckp/domain/veeam/vbo/vbo-successrate-list-domain.service';
import { StatusPerRange, TotalCapacity, Variations } from '../../../dashboard/models';
import { LoadBackupVboDashboardLastSessionRequested,
    LoadBackupVboDashboardLastSessionSucceeded, LoadBackupVboDashboardStatusPerRangeRequested, LoadBackupVboDashboardStatusPerRangeSucceeded,
    LoadBackupVboDashboardTicketingRequested,
    LoadBackupVboDashboardTicketingSucceeded,
    LoadBackupVboDashboardTotalCapacityRequested,
    LoadBackupVboDashboardTotalCapacitySucceeded,
    LoadBackupVboDashboardVariationsRequested,
    LoadBackupVboDashboardVariationsSucceeded
} from './vbo-dashboard.actions';
import { Status, VolumetricVariation } from '@apps/bckp/domain/models';
import { VboCapacityListDomainService } from '@apps/bckp/domain/veeam/vbo/vbo-capacity-list-domain.service';
import { RequestsSummaryCounter } from '@common/models';
import { RequestMiscDomainService } from '@apps/its2/domain';

@Injectable()
export class BckpVboDashboardEffects
{
    constructor (
        private actions: Actions,
        private vboSuccessRateListDomainService: VboSuccessRateListDomainService,
        private vboCapacityListDomainService: VboCapacityListDomainService,
        private requestMiscDomainService: RequestMiscDomainService
    )
    { }

    loadBackupVboDashboardStatusPerRangeRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVboDashboardStatusPerRangeRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vboSuccessRateListDomainService.getStatusPerRange(params.since, params.until)
                    .pipe(
                        map((statusPerDay: StatusPerDay[]) =>
                            LoadBackupVboDashboardStatusPerRangeSucceeded({
                                statusPerRange: {
                                    since: params.since,
                                    until: params.until,
                                    statusPerDay: statusPerDay,
                                    total: 0,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                } as StatusPerRange
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardLastSessionRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVboDashboardLastSessionRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vboSuccessRateListDomainService.getLastSession(params.since, params.until)
                    .pipe(
                        map((statuses: Status[]) =>
                            LoadBackupVboDashboardLastSessionSucceeded({
                                lastSession: {
                                    since: params.since,
                                    until: params.until,
                                    statuses: statuses,
                                    total: statuses[0].counter + statuses[1].counter + statuses[2].counter,
                                    expirationDate: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime })
                                }
                            })
                        )
                    )
            )
        )
    );

    loadBackupVboDashboardTotalCapacityRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVboDashboardTotalCapacityRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vboSuccessRateListDomainService.getTotalCapacity(params.since, params.until)
                    .pipe(
                        map((capacity: number) =>
                            LoadBackupVboDashboardTotalCapacitySucceeded({
                                totalCapacity: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    capacity: capacity / 1024
                                } as TotalCapacity
                            })
                        )
                    )
            )
        )
    );

    loadBackupDashboardTicketingRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVboDashboardTicketingRequested),
            switchMap(() =>
                this.requestMiscDomainService.getBckpSummary()
                    .pipe(
                        map((requestsSummaryCounter: RequestsSummaryCounter) =>
                            LoadBackupVboDashboardTicketingSucceeded({
                                ticketing: requestsSummaryCounter
                            })
                        )
                    )
            )
        )
    );

    loadBackupVboDashboardVariationsRequested = createEffect(() => this.actions
        .pipe(
            ofType(LoadBackupVboDashboardVariationsRequested),
            switchMap((params: { since: DateTime, until: DateTime }) =>
                this.vboCapacityListDomainService.getVolumetricVariation(params.since, params.until)
                    .pipe(
                        map((volumetricVariations: VolumetricVariation[]) =>
                            LoadBackupVboDashboardVariationsSucceeded({
                                variations: {
                                    since: params.since,
                                    until: params.until,
                                    validUntil: DateTime.utc().plus({ minutes: BckpConstants.DataLifeTime }),
                                    volumetricVariations: [...volumetricVariations]
                                } as Variations
                            })
                        )
                    )
            )
        )
    );
}
